import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { post } from '@/lib/api/client';
import { useAuthStore } from '@/features/auth/auth.store';

/**
 * Transport for the page-visit endpoints.
 *
 * `page-visits.ts` holds the policy — what counts as a visit, how long the
 * window is, how many visits make a habit. This file only knows how to say it
 * to the API.
 */

/**
 * Every visit this app records. Other Gopanza clients send their own value:
 * the API checks the shape and not the vocabulary, so a client-specific app
 * never needs a deploy there before it can start writing.
 */
const DOMAIN = 'admin-portal';

/**
 * One id per tab. Nothing reads it yet — it is there so the navigation
 * timeline the API has columns for can group a real session rather than
 * guessing from the gaps between timestamps.
 */
const SESSION_ID =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : undefined;

/** `TopPageVisit` in the Grocefy swagger. `page` is a nav key, never a route. */
export interface TopPageVisitDto {
  page: string;
  domain: string;
  count: number;
  lastVisitedOn: string;
}

/**
 * A UTC instant in the zoneless shape these endpoints bind as `DateTime`.
 *
 * Leaving the trailing `Z` on lets the server read it as its own local time
 * and shifts the whole range by the host's offset — the same class of bug as
 * the two timezone hotfixes. Deliberately not `toApiInstant` from
 * `lib/store-time`: that one is the store's wall clock, and this window is a
 * rolling thirty days of real time, not a span of the store's calendar days.
 */
function toUtcInstant(date: Date) {
  return date.toISOString().slice(0, 23);
}

/**
 * Records one landing.
 *
 * The only fire-and-forget POST in the app — no toast, no retry, failures
 * dropped on the floor. This is telemetry the user never asked for, and a page
 * that announces "could not record your visit" is worse than one that quietly
 * stops learning. That is the same trade the old localStorage write made when
 * the quota ran out.
 */
export function useRecordVisit() {
  const businessId = useAuthStore((s) => s.business?.id);

  return useMutation({
    mutationFn: (page: string) =>
      post<null>('users/pagevisits', {
        Visits: [
          {
            Domain: DOMAIN,
            Page: page,
            // Kept alongside the key, not instead of it: the key is what gets
            // ranked, the path is only there for a future timeline.
            Path: window.location.pathname,
            SessionId: SESSION_ID,
            // Null is Global, which is a real scope and not a missing value.
            BusinessId: businessId ?? null,
          },
        ],
      }),
    retry: false,
  });
}

/**
 * The account's most visited pages inside a trailing window.
 *
 * Counts come back with the keys so the ranking is auditable and the window
 * and threshold can be retuned from here without touching the API.
 */
export function useTopPages(options: {
  limit: number;
  windowDays: number;
  minCount: number;
}) {
  const { limit, windowDays, minCount } = options;
  const userId = useAuthStore((s) => s.profile?.id);

  return useQuery({
    queryKey: ['page-visits', 'top', DOMAIN, userId, limit, windowDays, minCount],
    enabled: Boolean(userId),
    queryFn: async () => {
      const from = new Date(Date.now() - windowDays * 86_400_000);
      const body = {
        StartDate: toUtcInstant(from),
        EndDate: null,
        Lifetime: false,
        Domain: DOMAIN,
        MinCount: minCount,
      };
      // OkPageData nests the array under `results` rather than returning it.
      const data = await post<{ results: TopPageVisitDto[] }>(
        `users/pagevisits/top/${limit}`,
        body
      );
      return data?.results ?? [];
    },
  });
}

/** `PageVisitEntry` in the Grocefy swagger. One landing, not an aggregate. */
export interface PageVisitEntryDto {
  page: string;
  domain: string;
  /** The URL as visited, entity ids and all. Null on rows written before it was kept. */
  path: string | null;
  /** The browser tab the visit came from. */
  sessionId: string | null;
  /** Encoded. Null is Global. */
  businessId: string | null;
  businessName: string | null;
  visitedOn: string;
}

/** One request's worth of history. The API caps it at 200. */
export const HISTORY_PAGE_SIZE = 50;

/**
 * One account's visits, newest first.
 *
 * The unaggregated form of `useTopPages`, for support looking at somebody
 * else. Reading another account is a customer-service call and the API checks
 * the caller's roles; reading your own needs nothing, so `me` works for anyone.
 *
 * `domain` narrows to one client app, and null spans all of them: an account
 * that uses more than one Gopanza app has one history across the lot, ordered
 * by when things happened rather than by which app they happened in.
 *
 * Paged with `useInfiniteQuery` rather than a page number: the list has no
 * total — `PageData` carries only the rows — and a short page is how the end
 * of the history announces itself.
 */
export function useUserPageHistory(
  userId: string | null,
  options: { windowDays: number | null; domain: string | null }
) {
  const { windowDays, domain } = options;

  return useInfiniteQuery({
    queryKey: ['page-visits', 'history', userId, windowDays, domain],
    enabled: Boolean(userId),
    // A wider range is the same list with more of it. Holding the rows already
    // on screen lets the panel dim while they load rather than collapsing to
    // skeletons and back.
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const body = {
        // Null days is "as far back as the API keeps them", which it answers
        // to Lifetime rather than to an absent range.
        StartDate:
          windowDays === null
            ? null
            : toUtcInstant(new Date(Date.now() - windowDays * 86_400_000)),
        EndDate: null,
        Lifetime: windowDays === null,
        Domain: domain,
        Skip: pageParam,
        Take: HISTORY_PAGE_SIZE,
      };
      const data = await post<{ results: PageVisitEntryDto[] }>(
        `users/${userId}/pagevisits/history`,
        body
      );
      return data?.results ?? [];
    },
    // A full page might be the last one; asking again is one request that
    // comes back empty, which is cheaper than tracking a count the API
    // does not return.
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < HISTORY_PAGE_SIZE
        ? undefined
        : allPages.reduce((total, page) => total + page.length, 0),
  });
}
